import { useCallback, useEffect, useRef, useState } from "react";
import { Package } from "lucide-react";
import { api } from "@/lib/api";

/* ============================================================================
 * Pemilih komponen paket saat input nota (brief bos 23 Agu 2026, poin 8).
 *
 * KENAPA berkas ini ada: Sales.jsx dan InputNota.jsx punya baris item yang
 * mirip tapi tersalin terpisah (`ItemNamaField`/`ItemImeiField` di kedua
 * berkas) — duplikasi itu sudah dua kali melahirkan penyimpangan di proyek
 * ini (lihat CLAUDE.md). Pemilih komponen paket BARU ini sengaja dibuat SATU
 * kali di sini dan dipakai kedua halaman, bukan disalin lagi.
 *
 * KONTRAK BACKEND yang WAJIB diikuti persis (routers/inventory/bundling.py
 * `validasi_dan_bangun_snapshot_komponen`, routers/sales.py `KomponenBundleItem`):
 * - Komponen ber-IMEI: SATU baris = SATU unit, qty SELALU 1, imei WAJIB
 *   diisi. Kalau resep butuh N unit komponen itu, harus ada N baris.
 * - Komponen TANPA IMEI: qty boleh agregat dalam satu baris (backend
 *   menjumlahkan), imei WAJIB kosong.
 * - Backend TIDAK menyimpan `has_imei` di dalam resep (`bundle_definitions.
 *   komponen[]` cuma {sku, nama, qty}) — makanya berkas ini menanyakannya
 *   sendiri lewat `GET /inventory/products/{sku}` per SKU komponen (di-cache
 *   lintas baris lewat `useProdukInfoCache`, supaya satu resep yang dipakai
 *   di banyak baris tidak menembak endpoint yang sama berulang-ulang).
 *
 * ATURAN #3 CLAUDE.md ("jangan tampilkan yang ujungnya ditolak"): backend
 * menolak KERAS (400) kalau komposisi tidak pas dengan resep atau IMEI belum
 * lengkap — `apakahKomponenLengkap`/`cariAlasanBundleBelumSiap` di bawah
 * dipakai Sales.jsx & InputNota.jsx untuk MENAHAN tombol Simpan dengan alasan
 * yang bisa dibaca kasir, sebelum sempat menyentuh API sama sekali.
 * ========================================================================== */

// ---------------------------------------------------------------------------
// Hook: daftar resep bundling AKTIF, kunci SKU (huruf besar) -> definisi
// (sudah termasuk `stok_virtual` kalau with_stok=true, bawaan endpoint).
// `active` biasanya `bolehLookupProduk && modalTerbuka` — dipanggil ulang
// tiap modal dibuka supaya angka stok paket yang dilihat kasir SELALU segar
// (poin 6 tugas: kasir harus tahu stok paket SEBELUM mengisi seluruh nota).
// ---------------------------------------------------------------------------
export function useResepBundlingAktif(active) {
  const [resepMap, setResepMap] = useState({});
  const [loadingResep, setLoadingResep] = useState(false);

  useEffect(() => {
    if (!active) return;
    let batal = false;
    (async () => {
      setLoadingResep(true);
      try {
        const { data } = await api.get("/inventory/bundling/definisi", { params: { with_stok: true } });
        if (batal) return;
        const map = {};
        (data.items || []).forEach((d) => {
          const key = (d.sku_bundle || "").trim().toUpperCase();
          if (key) map[key] = d;
        });
        setResepMap(map);
      } catch {
        // Diam-diam nonaktif (403 kalau role ini tidak punya inventory_produk.view,
        // atau error jaringan) — pola yang sama dengan ItemNamaField/ItemImeiField
        // di Sales.jsx & InputNota.jsx. Baris paket lalu diperlakukan seperti
        // barang biasa: SKU-nya tidak pernah ketahuan sebagai paket dari sisi UI
        // ini (kasir tanpa izin ini juga tidak bisa memilih SKU lewat autocomplete
        // sama sekali, lihat bolehLookupProduk di kedua halaman).
        if (!batal) setResepMap({});
      } finally {
        if (!batal) setLoadingResep(false);
      }
    })();
    return () => { batal = true; };
  }, [active]);

  return { resepMap, loadingResep };
}

// ---------------------------------------------------------------------------
// Hook: cache has_imei per SKU komponen, DIBAGI lintas seluruh baris item di
// satu form (satu instance dipanggil sekali di level halaman) — resep yang
// sama bisa dipakai di beberapa baris, dan beberapa resep bisa berbagi
// komponen yang sama, jadi satu SKU cukup ditanyakan sekali per sesi form.
// ---------------------------------------------------------------------------
export function useProdukInfoCache(bolehLookup) {
  const [cache, setCache] = useState({});
  const cacheRef = useRef({});
  const pendingRef = useRef(new Set());
  useEffect(() => { cacheRef.current = cache; }, [cache]);

  const ensureProdukInfo = useCallback((skus) => {
    if (!bolehLookup) return;
    const todo = [...new Set((skus || []).map((s) => (s || "").trim().toUpperCase()).filter(Boolean))]
      .filter((s) => !(s in cacheRef.current) && !pendingRef.current.has(s));
    if (!todo.length) return;
    todo.forEach((s) => pendingRef.current.add(s));
    todo.forEach(async (s) => {
      try {
        const { data } = await api.get(`/inventory/products/${encodeURIComponent(s)}`);
        setCache((c) => ({ ...c, [s]: { has_imei: !!data.has_imei, nama: data.nama } }));
      } catch {
        // GAGAL -> JANGAN dicache sama sekali (bukan ditebak "tanpa IMEI").
        // Baris yang menunggu info ini tetap tertahan (lihat apakahKomponenLengkap
        // "Memuat info komponen…") sampai berhasil dimuat ulang (mis. modal
        // ditutup-buka lagi) — lebih aman daripada menebak salah lalu kasir
        // menabrak 400 backend karena IMEI ternyata wajib.
      } finally {
        pendingRef.current.delete(s);
      }
    });
  }, [bolehLookup]);

  return { produkInfoCache: cache, ensureProdukInfo };
}

// Baris kosong sesuai bentuk resep — satu baris agregat per komponen, imei
// null. Dipakai `ItemNamaField.pilih()` di Sales.jsx/InputNota.jsx SEKALI
// setiap kali baris berpindah SKU (termasuk pindah dari satu paket ke paket
// lain, atau dari paket ke barang biasa — komposisi lama tidak relevan lagi).
export function bangunKomponenKosong(resep) {
  return (resep?.komponen || []).map((k) => ({ sku: k.sku, qty: Number(k.qty) || 0, imei: null }));
}

// Pecah/gabungkan slot komponen begitu has_imei sebuah SKU baru diketahui,
// supaya bentuknya selalu persis kontrak `KomponenBundleItem` (lihat docstring
// atas berkas ini). Sengaja idempoten: dipanggil ulang tiap render aman,
// mengembalikan REFERENSI `komponen` yang SAMA persis kalau tidak ada yang
// perlu diubah (dipakai caller untuk tahu apa perlu setState atau tidak).
export function normalisasiKomponenSlot(resep, komponen, produkInfoCache) {
  if (!resep) return komponen;
  let berubah = false;
  const hasil = [];
  for (const k of (resep.komponen || [])) {
    const kebutuhan = Number(k.qty) || 0;
    const key = (k.sku || "").trim().toUpperCase();
    const info = produkInfoCache[key];
    const existing = (komponen || []).filter((x) => x.sku === k.sku);
    if (info === undefined) {
      // Belum tahu has_imei komponen ini — JANGAN disentuh. `existing` bisa
      // saja sudah berisi IMEI yang dipilih kasir atau dimuat dari nota lama;
      // menimpanya di sini akan menghapusnya sebelum kita tahu itu memang
      // seharusnya begitu. Normalisasi sesungguhnya menunggu info resolve.
      if (existing.length) {
        hasil.push(...existing);
      } else {
        berubah = true;
        hasil.push({ sku: k.sku, qty: kebutuhan, imei: null });
      }
      continue;
    }
    if (info.has_imei) {
      const sudahPas = existing.length === kebutuhan && existing.every((x) => Number(x.qty) === 1);
      if (sudahPas) { hasil.push(...existing); continue; }
      berubah = true;
      const imeiLama = existing.map((x) => x.imei).filter(Boolean);
      for (let s = 0; s < kebutuhan; s++) {
        hasil.push({ sku: k.sku, qty: 1, imei: imeiLama[s] || null });
      }
    } else {
      const sudahPas = existing.length === 1 && Number(existing[0].qty) === kebutuhan && !existing[0].imei;
      if (sudahPas) { hasil.push(existing[0]); continue; }
      berubah = true;
      hasil.push({ sku: k.sku, qty: kebutuhan, imei: null });
    }
  }
  return berubah ? hasil : (komponen || []);
}

// Satu sumber kebenaran "apakah baris paket ini SUDAH SIAP dikirim ke
// backend" — dipakai baik di dalam picker (pesan inline) MAUPUN di
// save()/submit() kedua halaman (menahan tombol, aturan #3 CLAUDE.md).
export function apakahKomponenLengkap(resep, komponen, produkInfoCache) {
  if (!resep) {
    // Resep tidak (lagi) aktif. Kalau baris ini tidak pernah punya komponen,
    // ini memang bukan baris paket — lolos seperti biasa (kompat mundur,
    // aturan #5). Kalau PUNYA snapshot komponen (nota lama yang resepnya
    // baru saja dinonaktifkan), backend akan menolak KESELURUHAN baris kalau
    // dikirim ulang (`get_resep_aktif` di `validasi_dan_bangun_snapshot_komponen`
    // — lihat bundling.py) — tahan di sini dengan pesan yang jelas.
    if (Array.isArray(komponen) && komponen.length) {
      return { lengkap: false, alasan: "Resep paket untuk baris ini sudah tidak aktif — tidak bisa disimpan ulang. Hapus baris ini atau aktifkan kembali resepnya lewat menu Bundling." };
    }
    return { lengkap: true };
  }
  const sv = resep.stok_virtual;
  if (!(sv?.qty > 0)) {
    return {
      lengkap: false,
      alasan: `Stok paket "${resep.nama}" habis${sv?.pembatas ? ` (dibatasi komponen ${sv.pembatas})` : ""} — tidak bisa dijual sekarang.`,
    };
  }
  for (const k of (resep.komponen || [])) {
    const kebutuhan = Number(k.qty) || 0;
    const key = (k.sku || "").trim().toUpperCase();
    const info = produkInfoCache[key];
    if (info === undefined) {
      return { lengkap: false, alasan: `Memuat info komponen ${k.nama || k.sku}…` };
    }
    const rows = (komponen || []).filter((x) => x.sku === k.sku);
    const totalQty = rows.reduce((a, x) => a + (Number(x.qty) || 0), 0);
    if (totalQty !== kebutuhan) {
      return { lengkap: false, alasan: `Paket "${resep.nama}" butuh ${kebutuhan}x ${k.nama || k.sku} — baru terisi ${totalQty}.` };
    }
    if (info.has_imei) {
      const belum = rows.filter((x) => !x.imei || !String(x.imei).trim());
      if (belum.length) {
        return { lengkap: false, alasan: `Paket "${resep.nama}" butuh ${kebutuhan} IMEI untuk ${k.nama || k.sku} — belum semuanya dipilih.` };
      }
    }
  }
  return { lengkap: true };
}

// Kumpulan SEMUA IMEI yang sudah terpakai di form ini (baik di field IMEI
// baris biasa MAUPUN slot komponen paket) — dipakai supaya satu unit tidak
// bisa terpilih dua kali dalam satu transaksi, konsisten dengan
// `imeiTerpakaiLain` yang sudah ada di ItemImeiField.
export function kumpulkanSemuaImeiForm(items) {
  const s = new Set();
  (items || []).forEach((it) => {
    if (it.imei && String(it.imei).trim()) s.add(String(it.imei).trim());
    (it.komponen || []).forEach((k) => {
      if (k.imei && String(k.imei).trim()) s.add(String(k.imei).trim());
    });
  });
  return s;
}

// Alasan (kalau ada) baris paket PERTAMA yang belum siap dikirim — dipakai
// save()/submit() untuk menahan tombol Simpan (aturan #3 CLAUDE.md) sebelum
// menyentuh API sama sekali.
export function cariAlasanBundleBelumSiap(items, resepMap, produkInfoCache) {
  for (let i = 0; i < (items || []).length; i++) {
    const it = items[i];
    const sku = (it.sku || "").trim().toUpperCase();
    const resep = sku ? resepMap[sku] : null;
    const punyaSnapshot = Array.isArray(it.komponen) && it.komponen.length > 0;
    if (!resep && !punyaSnapshot) continue;
    const cek = apakahKomponenLengkap(resep, it.komponen, produkInfoCache);
    if (!cek.lengkap) return `Baris ${i + 1} (${it.nama_barang || sku || "-"}): ${cek.alasan}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Pemilih IMEI SATU slot komponen -> unit inventory. Sama pola dengan
// ItemImeiField (Sales.jsx/InputNota.jsx), bedanya sumber SKU-nya eksplisit
// (SKU komponen, bukan SKU baris) dan pemanggilnya generik lewat onPick/onClear
// alih-alih onPatch supaya tidak terikat ke bentuk state satu baris item.
// ---------------------------------------------------------------------------
function ComponentImeiSlot({ testidBase, sku, imei, onPick, onClear, imeiTerpakaiLain, bolehLookup }) {
  const [cari, setCari] = useState("");
  const [saran, setSaran] = useState([]);
  const [mencari, setMencari] = useState(false);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const cariUnit = (teks) => {
    setCari(teks);
    if (!bolehLookup) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = teks.trim();
    if (q.length < 3) { setSaran([]); setMencari(false); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++reqSeq.current;
      setMencari(true);
      try {
        const { data } = await api.get("/inventory/units/pilihan-jual", { params: { q, sku } });
        if (seq === reqSeq.current) setSaran(data.items || []);
      } catch {
        if (seq === reqSeq.current) setSaran([]);
      } finally {
        if (seq === reqSeq.current) setMencari(false);
      }
    }, 300);
  };

  if (imei) {
    return (
      <span data-testid={`${testidBase}-badge`}
        className="inline-flex items-center gap-1 text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
        IMEI {imei}
        <button type="button" onClick={onClear} title="Lepas pilihan IMEI"
          className="text-emerald-600 hover:text-emerald-900 leading-none">×</button>
      </span>
    );
  }

  return (
    <div className="relative">
      <input value={cari} onChange={(e) => cariUnit(e.target.value)}
        placeholder="Cari IMEI/SN dari stok…" data-testid={`${testidBase}-cari`}
        className="h-7 px-2 rounded border border-slate-200 text-[11px] w-full font-mono" />
      {saran.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
          {saran.map((u, n) => {
            const dipakai = imeiTerpakaiLain?.has(u.identifier);
            return (
              <button key={u.id} type="button" disabled={dipakai}
                onClick={() => { if (!dipakai) onPick(u.identifier); }}
                title={dipakai ? "Unit ini sudah dipilih di baris lain pada nota ini" : ""}
                data-testid={`${testidBase}-saran-${n}`}
                className={`w-full text-left px-2 py-1 text-[11px] border-b border-slate-50 last:border-0 ${
                  dipakai ? "bg-slate-50 opacity-60 cursor-not-allowed" : "hover:bg-emerald-50"}`}>
                <span className="font-mono font-medium text-slate-900">{u.identifier}</span>
                {u.warehouse_nama && <span className="text-slate-400 ml-1">· {u.warehouse_nama}</span>}
                {dipakai && <span className="text-rose-600 ml-1">sudah dipilih</span>}
              </button>
            );
          })}
        </div>
      )}
      {cari.trim().length >= 3 && !mencari && saran.length === 0 && (
        <p className="text-[10px] text-slate-400">Tidak ada unit tersedia yang cocok.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Komponen utama: satu baris item yang SKU-nya adalah sku_bundle resep
// aktif -> tampilkan rincian komponen + pemilih IMEI (poin 8-9-11).
// `resep` null/undefined berarti SKU baris ini bukan (atau tidak lagi) paket
// berresep aktif — komponen ini me-render null (tidak ada apa-apa) KECUALI
// baris masih membawa snapshot `komponen` lama (nota yang resepnya baru
// dinonaktifkan setelah dibuat).
// ---------------------------------------------------------------------------
export default function BundleKomponenPicker({
  index, resep, komponen, onChange, bolehLookupUnit, imeiTerpakaiLain,
  produkInfoCache, ensureProdukInfo,
}) {
  // Tanyakan has_imei tiap komponen SEKALI per resep yang sedang aktif di
  // baris ini (di-cache lintas baris, lihat useProdukInfoCache).
  useEffect(() => {
    if (resep?.komponen?.length) ensureProdukInfo(resep.komponen.map((k) => k.sku));
  }, [resep, ensureProdukInfo]);

  // Renormalisasi slot begitu info has_imei baru masuk ke cache. SENGAJA
  // tidak menyertakan `komponen`/`onChange` di deps: perubahan `komponen`
  // lewat pilih-IMEI manual (ubahSlot) sudah pasti dalam bentuk yang benar
  // (cuma mengisi field imei, tidak mengubah jumlah/qty baris), jadi tidak
  // perlu memicu normalisasi ulang — hanya resep berganti atau info baru
  // resolve yang perlu itu. `normalisasiKomponenSlot` sendiri membaca
  // `komponen` terbaru lewat closure tiap kali efek ini jalan.
  useEffect(() => {
    if (!resep) return;
    const next = normalisasiKomponenSlot(resep, komponen, produkInfoCache);
    if (next !== komponen) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resep, produkInfoCache]);

  if (!resep) {
    if (!komponen?.length) return null;
    return (
      <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[10px] text-amber-800"
        data-testid={`bundle-resep-nonaktif-${index}`}>
        Resep paket untuk produk ini sudah tidak aktif lagi — komposisi terakhir:{" "}
        {komponen.map((k) => `${k.nama || k.sku} ${k.imei ? `(IMEI ${k.imei})` : `×${k.qty}`}`).join(", ")}.
        Hapus baris ini atau aktifkan kembali resepnya lewat menu Bundling.
      </div>
    );
  }

  const stokVirtual = resep.stok_virtual || { qty: 0, pembatas: null, rincian: [] };
  const stokHabis = !(stokVirtual.qty > 0);
  const cek = apakahKomponenLengkap(resep, komponen, produkInfoCache);

  const ubahSlot = (idx, nilaiImei) => {
    const next = [...(komponen || [])];
    next[idx] = { ...next[idx], imei: nilaiImei };
    onChange(next);
  };

  return (
    <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50/60 p-2 space-y-1.5"
      data-testid={`bundle-info-${index}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-800">
          <Package className="w-3 h-3" /> Paket: {resep.nama}
        </span>
        <span data-testid={`bundle-stok-${index}`}
          className={`text-[10px] font-mono font-semibold ${stokHabis ? "text-rose-600" : "text-emerald-700"}`}>
          Stok paket: {stokVirtual.qty}
        </span>
      </div>
      {stokHabis ? (
        <p className="text-[10px] text-rose-600" data-testid={`bundle-alasan-${index}`}>
          Stok paket habis{stokVirtual.pembatas ? ` — dibatasi komponen ${stokVirtual.pembatas}` : ""}. Tidak bisa dijual sekarang.
        </p>
      ) : (
        <>
          <div className="space-y-1.5">
            {resep.komponen.map((k) => {
              const info = produkInfoCache[(k.sku || "").trim().toUpperCase()];
              const slots = (komponen || [])
                .map((x, idx) => ({ ...x, _idx: idx }))
                .filter((x) => x.sku === k.sku);
              if (info === undefined) {
                return <p key={k.sku} className="text-[10px] text-slate-400">Memuat info {k.nama || k.sku}…</p>;
              }
              if (!info.has_imei) {
                return (
                  <div key={k.sku} className="flex items-center justify-between text-[10px] text-slate-600"
                    data-testid={`bundle-komponen-info-${index}-${k.sku}`}>
                    <span>{k.nama || k.sku} × {k.qty}</span>
                    <span className="text-slate-400">tanpa IMEI</span>
                  </div>
                );
              }
              return (
                <div key={k.sku} className="space-y-1">
                  <p className="text-[10px] text-slate-600">{k.nama || k.sku} — pilih {k.qty} IMEI</p>
                  {slots.map((slot, n) => (
                    <ComponentImeiSlot key={n}
                      testidBase={`bundle-komponen-imei-${index}-${n}-${k.sku}`}
                      sku={k.sku} imei={slot.imei}
                      onPick={(v) => ubahSlot(slot._idx, v)}
                      onClear={() => ubahSlot(slot._idx, null)}
                      imeiTerpakaiLain={imeiTerpakaiLain}
                      bolehLookup={bolehLookupUnit} />
                  ))}
                </div>
              );
            })}
          </div>
          {!cek.lengkap && (
            <p className="text-[10px] text-amber-700" data-testid={`bundle-alasan-${index}`}>{cek.alasan}</p>
          )}
        </>
      )}
    </div>
  );
}
